import { useEffect, useState } from 'react'
import './App.css'
import { AppShell, Text, Box, Burger, Group, Stack, Divider, Center, Image, Flex, Button, ScrollArea, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link, Outlet, useMatch, useMatchRoute, useNavigate, useParams, useRouterState, useSearch } from '@tanstack/react-router';
import { useAuthStore } from './Stores/AuthStore';
import { getApiAuthState } from './API';
import type { AxiosError } from 'axios';
import { NavigationButton } from './Components/NavigationButton';
import { LoginButton, LogoutButton } from './Auth/LoginPage';
import { IconBell, IconCarambola, IconCloud, IconDatabase, IconFlask, IconHome, IconList, IconLogin, IconMessages, IconPlugConnected, IconRobot, IconSettings } from '@tabler/icons-react';
import { UseWorkspaceAuth } from './Workspaces/UseWorkspaceAuth';
function AdminNavigation(props: { onClickLink?: () => void }) {
  return <>
    <NavigationButton match={{ to: "/admin/userManager", fuzzy: true }} onClickLink={props.onClickLink}><IconList style={{ marginRight: "10px" }} />使用者管理</NavigationButton>
    <NavigationButton match={{ to: "/admin/workspaceManager", fuzzy: true }} onClickLink={props.onClickLink}><IconCloud style={{ marginRight: "10px" }} />工作空間管理</NavigationButton>
  </>
}
function UserNavigation(props: { onClickLink?: () => void }) {
  const match = useMatchRoute()
  const workspaceId = (match({ from: "/workspace/$workspaceId/", fuzzy: true }) as any).workspaceId
  const viewerAuth = UseWorkspaceAuth(workspaceId, "Viewer")

  return <>
    <NavigationButton match={{ to: "/workspace", fuzzy: true }} onClickLink={props.onClickLink}><IconCloud style={{ marginRight: "10px" }} />工作空間</NavigationButton>
    {workspaceId && viewerAuth &&
      <Group gap={0} w="100%" py={10}>
        <Divider size="sm" ml={10} orientation="vertical" />
        <Stack gap={5} flex={1}>
          <NavigationButton match={{
            to: "/workspace/$workspaceId/embeddingCollection", fuzzy: true, params: {
              workspaceId: workspaceId
            }
          }} onClickLink={props.onClickLink}><IconDatabase style={{ marginRight: "10px" }} />資料集合</NavigationButton>
          <NavigationButton match={{
            to: "/workspace/$workspaceId/chatBotData", fuzzy: true, params: {
              workspaceId: workspaceId
            }
          }} onClickLink={props.onClickLink}><IconRobot style={{ marginRight: "10px" }} />機器人設定</NavigationButton>
          <NavigationButton match={{
            to: "/workspace/$workspaceId/botProvider", fuzzy: true, params: {
              workspaceId: workspaceId
            }
          }} onClickLink={props.onClickLink}><IconPlugConnected style={{ marginRight: "10px" }} />機器人串接</NavigationButton>
          <NavigationButton match={{
            to: "/workspace/$workspaceId/notificationSettings", fuzzy: true, params: {
              workspaceId: workspaceId
            }
          }} onClickLink={props.onClickLink}><IconBell style={{ marginRight: "10px" }} />通知設定</NavigationButton>
          <NavigationButton match={{
            to: "/workspace/$workspaceId/chatEvaluationTestSet", fuzzy: true, params: {
              workspaceId: workspaceId
            }
          }} onClickLink={props.onClickLink}><IconFlask style={{ marginRight: "10px" }} />聊天評估</NavigationButton>
        </Stack>
      </Group>
    }
    <NavigationButton match={{ to: "/chatCreditKey", fuzzy: true }} onClickLink={props.onClickLink}><IconCarambola style={{ marginRight: "10px" }} />聊天點數</NavigationButton>
    <NavigationButton match={{ to: "/settings", fuzzy: true }} onClickLink={props.onClickLink}><IconSettings style={{ marginRight: "10px" }} />個人設定</NavigationButton>
  </>
}
function Navigation(props: { onClickLink?: () => void }) {
  const auth = useAuthStore()
  const search = useSearch({ strict: false })
  return <>
    {/* <Button variant="subtle" onClick={() => { navigate({ to: "/" }); }}>首頁</Button> */}
    <Flex w="100%" h="100%" direction="column" justify='space-between'>
      <ScrollArea>
        <NavigationButton match={{ to: "/" }} onClickLink={props.onClickLink}><IconHome style={{ marginRight: "10px" }} />首頁</NavigationButton>
        {auth.data.role == "admin" && <AdminNavigation onClickLink={props.onClickLink} />}
        {auth.data.role == "user" && <UserNavigation onClickLink={props.onClickLink} />}
      </ScrollArea>
      <Box>
        <Stack align="center" gap={0}>
          <Text size="xs" c="gray">
            Powered by
          </Text>
          <Text size="xs" c="gray">
            Tempest Digital Co., Ltd.
          </Text>
        </Stack>
        <Divider my="lg" />
        <Center>
          <Tooltip openDelay={500} color="gray" label={auth.data.displayName || auth.data.userName}>
            <Text fw={700} w="80%" ta="center" style={{ fontSize: "20px" }} truncate="end" variant="gradient" mb={10} gradient={{ from: 'blue', to: 'cyan', deg: 90 }}>
              {auth.data.displayName || auth.data.userName}
            </Text>
          </Tooltip>
        </Center>
        {!auth.data.userName ? <LoginButton redirect={search?.redirect as any} openOnStart={!!search?.redirect} >
          {
            ({ opened, open }) =>
              <Button w="100%" variant="subtle" onClick={() => {
                open();
              }} ><IconLogin style={{ marginRight: "10px" }} />登入</Button>
          }
        </LoginButton> : <LogoutButton variant="light" color="gray" />}
      </Box>
    </Flex>
  </>
}

export default function App() {
  const auth = useAuthStore()
  const router = useRouterState();
  const matchRoute = useMatchRoute()
  const navigate = useNavigate()
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);
  var publicChat = matchRoute({ to: "/workspace/$workspaceId/chatBotData/$chatBotDataId/publicChat" })
  var evalReport = matchRoute({ to: "/workspace/$workspaceId/chatEvaluationTestSet/$chatEvaluationTestSetId/test/$chatEvaluationTestId/report", fuzzy: true })
  const withoutShell = !!publicChat || !!evalReport
  useEffect(() => {
    auth.SetUpdating(true)
    try {
      getApiAuthState().then(result => {
        if (result.data.userName && result.data.role)
          auth.Update(result.data)
      }).catch((e: AxiosError) => {
        var index = matchRoute({ to: "/" })
        var adminLogin = matchRoute({ to: "/admin/Login" })
        if (e.status == 401 && !index && !adminLogin && !publicChat) {
          navigate({ to: "/", search: { redirect: router.location.pathname } })
        }
      })
    } catch (e) {

    }
    auth.SetUpdating(false)
  }, [])

  return (
    <>
      {
        withoutShell ? <Outlet /> :
          <AppShell
            header={{ height: 40 }}
            navbar={{
              width: 200,
              breakpoint: 'sm',
              collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
            }}
            padding="lg"
          >
            <AppShell.Header>
              <Group h="100%" px="md">
                <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
                <Burger opened={desktopOpened} onClick={toggleDesktop} visibleFrom="sm" size="sm" />
                <Flex flex={1} direction="row" justify="start">
                  <Link to='/'>
                    {/* <Image h={30} fit="contain" src={banner} /> */}
                  </Link>
                </Flex>
              </Group>
            </AppShell.Header>
            <AppShell.Navbar py="md" px={4} style={{ boxShadow: "var(--mantine-shadow-xl)" }}>
              <Navigation onClickLink={() => {
                if (mobileOpened) toggleMobile();
              }} />
            </AppShell.Navbar>

            <AppShell.Main bg="#f4f4f4">
              <Outlet />
            </AppShell.Main>
          </AppShell>
      }
    </>
  );
}